import React from "react";
import Select from "components/ui/Select";

type Option = { id: number; name: string; stage_id?: number | null };
type SubjectOption = Option & { stage_ids?: number[]; grade_id?: number | null; is_active?: boolean };

type Props = {
  isArabic: boolean;
  stages: Option[];
  grades: Option[];
  subjects: SubjectOption[];
  stageId: number | null;
  gradeId: number | null;
  subjectId: number | null;
  onStageChange: (id: number | null) => void;
  onGradeChange: (id: number | null) => void;
  onSubjectChange: (id: number | null) => void;
};

const toId = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
};

export default function BasicAcademicAssignmentSelects({
  isArabic,
  stages,
  grades,
  subjects,
  stageId,
  gradeId,
  subjectId,
  onStageChange,
  onGradeChange,
  onSubjectChange,
}: Props) {
  const gradeOptions = React.useMemo(
    () => grades.filter((grade) => toId(grade.stage_id) === stageId),
    [grades, stageId]
  );

  const subjectOptions = React.useMemo(
    () =>
      subjects.filter(
        (subject) =>
          subject.is_active !== false &&
          (subject.stage_ids?.length ? subject.stage_ids : [subject.stage_id]).some((id) => toId(id) === stageId)
      ),
    [subjects, stageId]
  );

  const noStages = stages.length === 0;
  const noGrades = stageId !== null && gradeOptions.length === 0;
  const noSubjects = stageId !== null && subjectOptions.length === 0;

  return (
    <>
      <Select
        label={isArabic ? "المرحلة *" : "Stage *"}
        value={stageId}
        onChange={(value) => onStageChange(toId(value))}
        options={stages.map((stage) => ({ value: stage.id, label: stage.name }))}
        isArabic={isArabic}
        numeric
        emptyValue={null}
        allowEmpty={stages.length > 1}
        required
        disabled={noStages}
        placeholder={isArabic ? "اختر المرحلة" : "Select stage"}
        helperText={noStages ? (isArabic ? "لا توجد مراحل معينة لحسابك" : "No stages are assigned to your account") : undefined}
        error={noStages}
      />

      <Select
        label={isArabic ? "الصف *" : "Grade *"}
        value={gradeId}
        onChange={(value) => onGradeChange(toId(value))}
        options={gradeOptions.map((grade) => ({ value: grade.id, label: grade.name }))}
        isArabic={isArabic}
        numeric
        emptyValue={null}
        allowEmpty={gradeOptions.length > 1}
        required
        disabled={stageId === null || noGrades}
        placeholder={isArabic ? "اختر الصف" : "Select grade"}
        helperText={
          noGrades
            ? isArabic
              ? "لا توجد صفوف معينة لهذه المرحلة"
              : "No assigned grades for this stage"
            : undefined
        }
        error={noGrades}
      />

      <Select
        label={isArabic ? "المادة *" : "Subject *"}
        value={subjectId}
        onChange={(value) => onSubjectChange(toId(value))}
        options={subjectOptions.map((subject) => ({ value: subject.id, label: subject.name }))}
        isArabic={isArabic}
        numeric
        emptyValue={null}
        allowEmpty={subjectOptions.length > 1}
        required
        disabled={stageId === null || noSubjects}
        placeholder={isArabic ? "اختر المادة" : "Select subject"}
        helperText={
          noSubjects
            ? isArabic
              ? "لا توجد مواد معينة لهذه المرحلة"
              : "No assigned subjects for this stage"
            : undefined
        }
        error={noSubjects}
      />
    </>
  );
}
